// ABOUTME: Email change verification page component for Oráculo dos 12 Raios
// ABOUTME: Handles email change token validation and user feedback (TD3-129 LGPD compliance)

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../services/pocketbaseClient';
import './EmailChangeVerification.css';

const EmailChangeVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('awaiting-password'); // awaiting-password, confirming, success, error
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = searchParams.get('token');

  // Verify token exists on mount
  useEffect(() => {
    if (!token) {
      console.log('❌ No token found in URL');
      setStatus('error');
      setMessage('Token de confirmação inválido ou ausente.');
    } else {
      console.log('🔮 Email Change Verification - Token: Present');
      setStatus('awaiting-password');
    }
  }, [token]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      setMessage('Por favor, digite sua senha.');
      return;
    }

    setIsSubmitting(true);
    setStatus('confirming');
    setMessage('Confirmando alteração de email...');

    try {
      console.log('⏳ Confirming email change with PocketBase...');

      // Confirm email change with PocketBase (requires password for security)
      await pb.collection('OracleUsers').confirmEmailChange(token, password);

      console.log('✅ Email change confirmed successfully!');

      // Immediately logout user (all sessions must be invalidated for security)
      console.log('🚪 Logging out user - email changed, re-authentication required');
      pb.authStore.clear();

      // Notify other tabs to logout (cross-tab synchronization)
      if (typeof BroadcastChannel !== 'undefined') {
        const channel = new BroadcastChannel('auth_channel');
        channel.postMessage({ type: 'LOGOUT', reason: 'email_change' });
        channel.close();
        console.log('📢 Broadcasted logout message to other tabs');
      }

      setStatus('success');
      setMessage('Email alterado com sucesso! Por favor, faça login novamente com seu novo email.');

      // Redirect to auth page after 3 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error) {
      console.error('❌ Email change confirmation error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        data: error.data
      });

      let errorMessage = 'Erro ao confirmar alteração de email. Verifique sua senha e tente novamente.';

      if (error.message && error.message.includes('token')) {
        errorMessage = 'Token de confirmação expirado ou inválido.';
      } else if (error.message && error.message.includes('password')) {
        errorMessage = 'Senha incorreta. Tente novamente.';
      } else if (error.message && error.message.includes('already')) {
        errorMessage = 'Esta alteração de email já foi confirmada anteriormente.';
      }

      console.log('📝 Setting error message:', errorMessage);
      setStatus('awaiting-password');
      setMessage(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth');
  };

  return (
    <div className="email-verification-container">
      {/* Mobile warning */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main verification content */}
      <div className="verification-main">
        <div className="verification-card">
          <div className="verification-icon">
            {status === 'confirming' && <span className="icon-spinner">🔮</span>}
            {status === 'success' && <span className="icon-success">✨</span>}
            {status === 'error' && <span className="icon-error">⚠️</span>}
            {status === 'awaiting-password' && <span className="icon-key">🔑</span>}
          </div>

          <h1>Confirmação de Alteração de Email</h1>

          {/* Password Input Form */}
          {status === 'awaiting-password' && (
            <>
              <p style={{ marginBottom: '20px', color: '#374151' }}>
                Por segurança, confirme sua senha para completar a alteração de email:
              </p>

              <form onSubmit={handlePasswordSubmit} style={{ width: '100%' }}>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Digite sua senha"
                    className="password-input"
                    disabled={isSubmitting}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '8px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      color: '#1F2937',
                      backgroundColor: '#FFFFFF'
                    }}
                  />
                </div>

                {message && (
                  <div className="verification-message error" style={{ marginBottom: '20px' }}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || !password}
                  className="back-to-login-btn primary-cta"
                  style={{ width: '100%' }}
                >
                  {isSubmitting ? 'Confirmando...' : 'Confirmar Alteração'}
                </button>
              </form>
            </>
          )}

          {/* Confirming State */}
          {status === 'confirming' && (
            <div className={`verification-message verifying`}>
              {message}
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className={`verification-message success`}>
                {message}
              </div>
              <div className="verification-progress">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <p>Redirecionando para o login...</p>
              </div>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className={`verification-message error`}>
                {message}
              </div>
              <button
                onClick={handleBackToLogin}
                className="back-to-login-btn primary-cta"
              >
                Voltar para Login
              </button>
            </>
          )}
        </div>

        <footer className="verification-footer">
          <p>Oráculo dos 12 Raios • v1.0 • by Buenaar</p>
        </footer>
      </div>
    </div>
  );
};

export default EmailChangeVerification;
